import type { IRequestRepository } from '@/domain/repositories/request.repository'
import type {
  Request,
  CreateRequestInput,
  GetRequestsParams,
  RequestListResult,
} from '@/domain/entities/request.entity'
import { requestsContract } from '@bibleawr/landing2-contract'
import { createLandingTsRestClient } from '../api/ts-rest-client'
import { toStringOrUndefined } from '@/utils/value.helpers'

function mapResponseToRequest (dto: {
  _id: string
  created_at?: string | Date
  request_user_name?: string
  request_user_phone?: string
  request_user_email?: string
  request_user_region_id?: string
  request_user_region_locality?: string
  request_user_progress?: unknown
  request_user_notes?: string
  request_course_name?: string
  request_course_id?: string
  request_account_id?: string
  request_course_title?: string
  request_status?: string
  request_test_results?: unknown
  request_source?: string
}): Request {
  return {
    _id: dto._id,
    created_at: dto.created_at ? new Date(dto.created_at) : undefined,
    request_user_name: dto.request_user_name,
    request_user_phone: dto.request_user_phone,
    request_user_email: dto.request_user_email,
    request_user_region_id: dto.request_user_region_id,
    request_user_region_locality: dto.request_user_region_locality,
    request_user_progress: dto.request_user_progress,
    request_user_notes: dto.request_user_notes,
    request_course_name: dto.request_course_name,
    request_course_id: dto.request_course_id,
    request_account_id: dto.request_account_id,
    request_course_title: dto.request_course_title,
    request_status: dto.request_status,
    request_test_results: dto.request_test_results,
    request_source: dto.request_source,
  }
}

export class RequestRepository implements IRequestRepository {
  private readonly client = createLandingTsRestClient(requestsContract)

  async findMany (params?: GetRequestsParams): Promise<RequestListResult> {
    const res = await this.client.getRequests({
      query: {
        cursor: params?.cursor,
        limit: params?.limit,
        account_id: params?.account_id,
        phone: params?.phone,
        email: params?.email,
        created_at: params?.created_at,
      },
    })
    if (res.status !== 200) throw new Error(`Failed to fetch requests: ${res.status}`)

    const items = (res.body.data ?? []).map(mapResponseToRequest)
    const pagination = res.body.pagination
    const limit = pagination?.limit ?? params?.limit ?? 20
    const cursor = toStringOrUndefined(pagination?.cursor)
    const hasNextPage = pagination?.hasNextPage ?? items.length >= limit

    return { items, cursor, limit, hasNextPage }
  }

  async checkExists (phone?: string, email?: string): Promise<boolean> {
    if (!phone && !email) throw new Error('Phone or email is required')
    const res = await this.client.checkRequest({ query: { phone, email } })
    if (res.status !== 200) throw new Error(`Failed to check request: ${res.status}`)
    return res.body.data?.exists ?? false
  }

  async create (body: CreateRequestInput): Promise<Request> {
    const res = await this.client.createRequest({ body: body as never })
    if (res.status !== 200 && res.status !== 201) throw new Error(`Failed to create request: ${res.status}`)
    if (!res.body.data) throw new Error('Failed to create request: empty response')
    return mapResponseToRequest(res.body.data)
  }
}
